import { type ReactNode, Suspense, lazy, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Navigate, Route, Routes, useLocation } from 'react-router-dom'

import { trackOpenAiAdsPageViewed } from '@/api/deferredAnalytics'
import { trackPageview } from '@/api/tracking'
import { RouterLandingPage } from '@/features/router-landing/RouterLandingPage'
import { SeoKeywordLandingPage } from '@/features/seo-keyword-landing/SeoKeywordLandingPage'
import { SEO_KEYWORD_LANDINGS } from '@/features/seo-keyword-landing/catalog'
import { ViasAcompanamientoPage } from '@/features/vias-acompanamiento/ViasAcompanamientoPage'
import { useCookieConsent } from '@/hooks/useCookieConsent'
import { PARTNER_NOINDEX_PATHS } from '@/partner/constants'

import { AsesoriaPage } from '../pages/AsesoriaPage'
import { AuthorPage } from '../pages/AuthorPage'
import { BusinessPage } from '../pages/Business'
import { FaqPage } from '../pages/FaqPage'
import { FoodCostPage } from '../pages/FoodCost'
import { LearnArticlePage } from '../pages/LearnArticlePage'
import { LearnPage } from '../pages/LearnPage'
import { LearnTopicPage } from '../pages/LearnTopicPage'
import { NewsArticlePage } from '../pages/NewsArticlePage'
import { NewsPage } from '../pages/NewsPage'
import { NewsletterConfirmPage } from '../pages/NewsletterConfirmPage'
import { NotFoundPage } from '../pages/NotFoundPage'
import { PressPage } from '../pages/PressPage'
import { SchedulePage } from '../pages/SchedulePage'
import {
    CookiePolicyPage,
    DataProcessingAgreementPage,
    PrivacyPolicyPage,
    TermsAndConditionsPage,
} from '../pages/legal/LegalPages'
import { NOINDEX_ROUTES, ROUTES } from './constants'
import { parseLocalePath, publicRoutePath } from './locale'

const PartnerRoutes = lazy(() => import('@/partner/PartnerRoutes').then(module => ({ default: module.PartnerRoutes })))

const PUBLIC_PAGE_ROUTES: { path: string; element: ReactNode }[] = [
    { path: ROUTES.home, element: <FoodCostPage /> },
    { path: ROUTES.ponloEnOrden, element: <ViasAcompanamientoPage /> },
    { path: ROUTES.empieza, element: <RouterLandingPage /> },
    { path: ROUTES.invest, element: <BusinessPage /> },
    { path: ROUTES.asesoria, element: <AsesoriaPage /> },
    { path: ROUTES.faqs, element: <FaqPage /> },
    { path: ROUTES.press, element: <PressPage /> },
    { path: ROUTES.learn, element: <LearnPage /> },
    { path: ROUTES.learnEscandallos, element: <LearnTopicPage topic='Escandallos' /> },
    { path: ROUTES.learnBeneficio, element: <LearnTopicPage topic='Beneficio' /> },
    { path: ROUTES.learnInventario, element: <LearnTopicPage topic='Inventario' /> },
    { path: ROUTES.learnEmprenderEnHosteleria, element: <LearnTopicPage topic='Emprender en hostelería' /> },
    { path: ROUTES.learnArticle, element: <LearnArticlePage /> },
    { path: ROUTES.news, element: <NewsPage /> },
    { path: ROUTES.newsArticle, element: <NewsArticlePage /> },
    { path: ROUTES.author, element: <AuthorPage /> },
    ...SEO_KEYWORD_LANDINGS.map(landing => ({
        path: landing.path,
        element: <SeoKeywordLandingPage landing={landing} />,
    })),
    { path: ROUTES.cookiePolicy, element: <CookiePolicyPage /> },
    { path: ROUTES.privacyPolicy, element: <PrivacyPolicyPage /> },
    { path: ROUTES.termsAndConditions, element: <TermsAndConditionsPage /> },
    { path: ROUTES.dataProcessingAgreement, element: <DataProcessingAgreementPage /> },
    { path: ROUTES.schedule, element: <SchedulePage /> },
    { path: ROUTES.newsletterConfirm, element: <NewsletterConfirmPage /> },
]

function mixpanelPageSlug(pathname: string): string {
    const { pathname: localeFree } = parseLocalePath(pathname)
    if (localeFree === ROUTES.home) return 'food-cost'
    const slug = localeFree.split('/').filter(Boolean)[0]
    return slug || 'home'
}

function LocaleSync({ children }: { children: ReactNode }) {
    const location = useLocation()
    const { i18n } = useTranslation()
    const { locale } = parseLocalePath(location.pathname)

    useEffect(() => {
        if (i18n.language !== locale) {
            i18n.changeLanguage(locale)
        }
    }, [i18n, locale])

    return children
}

const SPANISH_ONLY_PUBLIC_ROUTES = new Set<string>([ROUTES.ponloEnOrden, ROUTES.asesoria, ROUTES.empieza])

/** Spanish-only funnel: keep the EN hosting rewrite, then bounce to ES. */
function RedirectEnToEsPath({ path }: { path: string }) {
    const { search, hash } = useLocation()
    return <Navigate to={`${path}${search}${hash}`} replace />
}

export const RoutingComponent = () => {
    const location = useLocation()
    const { pathname: localeFreePath } = parseLocalePath(location.pathname)
    const { preferences } = useCookieConsent()

    useEffect(() => {
        if (!preferences.measurement) return
        trackPageview({ page: mixpanelPageSlug(location.pathname) })
    }, [location.pathname, preferences.measurement])

    useEffect(() => {
        if (!preferences.marketing) return
        const pageId = mixpanelPageSlug(location.pathname)
        trackOpenAiAdsPageViewed({ id: pageId, name: location.pathname })
    }, [location.pathname, preferences.marketing])

    useEffect(() => {
        if (location.hash) return
        window.scrollTo(0, 0)
    }, [location.pathname, location.hash])

    const shouldNoindex =
        NOINDEX_ROUTES.some(path => path === localeFreePath) ||
        PARTNER_NOINDEX_PATHS.some(path => path === location.pathname)

    return (
        <LocaleSync>
            {shouldNoindex ? (
                <Helmet>
                    <meta name='robots' content='noindex,nofollow' />
                </Helmet>
            ) : null}
            <Routes>
                <Route path='/en/socios/*' element={<Navigate to='/socios' replace />} />
                <Route path='/en/ponlo-en-orden' element={<RedirectEnToEsPath path={ROUTES.ponloEnOrden} />} />
                <Route path='/en/asesoria' element={<RedirectEnToEsPath path={ROUTES.asesoria} />} />
                <Route path='/en/empieza' element={<RedirectEnToEsPath path={ROUTES.empieza} />} />
                <Route
                    path='/socios/*'
                    element={
                        <Suspense fallback={null}>
                            <PartnerRoutes />
                        </Suspense>
                    }
                />
                {PUBLIC_PAGE_ROUTES.flatMap(({ path, element }) => {
                    const esRoute = <Route key={`es-${path}`} path={publicRoutePath(path, 'es')} element={element} />
                    if (SPANISH_ONLY_PUBLIC_ROUTES.has(path)) return [esRoute]
                    return [esRoute, <Route key={`en-${path}`} path={publicRoutePath(path, 'en')} element={element} />]
                })}
                <Route path='*' element={<NotFoundPage />} />
            </Routes>
        </LocaleSync>
    )
}
